import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatMessage, ChatPosition } from '../types';
import { WidgetChatMessage } from './chat-message';
import { WidgetTypingIndicator } from './typing-indicator';

type ChatListProps = {
    messages: ChatMessage[];
    loading: boolean;
    typing: boolean;
    welcomeMessage?: string | null;
    onRetry: (id: string) => void;
};

const dayFormat = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
});

const GROUP_INTERVAL_MS = 120 * 1000;

function positionFor(index: number, messages: ChatMessage[]): ChatPosition {
    const prev = messages[index - 1];
    const next = messages[index + 1];
    const current = messages[index];
    const starts =
        !prev ||
        prev.sender !== current.sender ||
        isNewDay(prev, current) ||
        current.timestamp - prev.timestamp > GROUP_INTERVAL_MS;
    const ends =
        !next ||
        next.sender !== current.sender ||
        isNewDay(current, next) ||
        next.timestamp - current.timestamp > GROUP_INTERVAL_MS;

    if (starts && ends) {
        return 'solo';
    }

    if (starts) {
        return 'first';
    }

    if (ends) {
        return 'last';
    }

    return 'middle';
}

function isNewDay(
    prev: ChatMessage | undefined,
    current: ChatMessage,
): boolean {
    if (!prev) {
        return true;
    }

    const a = new Date(prev.timestamp);
    const b = new Date(current.timestamp);

    return (
        a.getFullYear() !== b.getFullYear() ||
        a.getMonth() !== b.getMonth() ||
        a.getDate() !== b.getDate()
    );
}

function dayLabel(timestamp: number): string {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();

    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    if (sameDay(date, today)) {
        return 'Today';
    }

    if (sameDay(date, yesterday)) {
        return 'Yesterday';
    }

    return dayFormat.format(date);
}

export function WidgetChatList({
    messages,
    loading,
    typing,
    welcomeMessage,
    onRetry,
}: ChatListProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [unseenCount, setUnseenCount] = useState(0);
    const prevLengthRef = useRef(messages.length);

    const scrollToBottom = useCallback(
        (behavior: ScrollBehavior = 'smooth') => {
            const el = containerRef.current;

            if (!el) {
                return;
            }

            el.scrollTo({ top: el.scrollHeight, behavior });
            setIsAtBottom(true);
            setUnseenCount(0);
        },
        [],
    );

    useEffect(() => {
        const el = containerRef.current;

        if (!el) {
            return;
        }

        const onScroll = () => {
            const atBottom =
                el.scrollHeight - el.scrollTop - el.clientHeight <= 60;
            setIsAtBottom(atBottom);

            if (atBottom) {
                setUnseenCount(0);
            }
        };
        el.addEventListener('scroll', onScroll, { passive: true });

        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (messages.length > prevLengthRef.current) {
            if (isAtBottom) {
                scrollToBottom('smooth');
            } else {
                setUnseenCount(
                    (c) => c + (messages.length - prevLengthRef.current),
                );
            }
        }

        prevLengthRef.current = messages.length;
    }, [messages.length, isAtBottom, scrollToBottom]);

    useEffect(() => {
        scrollToBottom('instant');
    }, [scrollToBottom]);

    useEffect(() => {
        if (typing && isAtBottom) {
            scrollToBottom('smooth');
        }
    }, [typing, isAtBottom, scrollToBottom]);

    return (
        <>
            <div
                ref={containerRef}
                className="op-messages"
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
            >
                {loading && (
                    <div className="op-loading" aria-label="Connecting to chat">
                        <div className="op-shimmer op-shimmer-in" />
                        <div className="op-shimmer op-shimmer-out" />
                        <div className="op-shimmer op-shimmer-in op-shimmer-short" />
                    </div>
                )}
                {!loading && messages.length === 0 && (
                    <div className="op-welcome">
                        <p className="op-welcome-sub">
                            {welcomeMessage ??
                                'Welcome! Send a message below to start chatting.'}
                        </p>
                    </div>
                )}
                {messages.map((message, index) => {
                    const prev = messages[index - 1];
                    const showDivider = isNewDay(prev, message);
                    const position = positionFor(index, messages);
                    const showTime = position === 'solo' || position === 'last';

                    return (
                        <div key={message.id}>
                            {showDivider && (
                                <div className="op-day">
                                    <span>{dayLabel(message.timestamp)}</span>
                                </div>
                            )}
                            <WidgetChatMessage
                                message={message}
                                position={position}
                                showTime={showTime}
                                onRetry={onRetry}
                            />
                        </div>
                    );
                })}
                {!loading && typing && <WidgetTypingIndicator />}
            </div>
            {!isAtBottom && (
                <button
                    type="button"
                    className="op-jump"
                    onClick={() => scrollToBottom('smooth')}
                    aria-label={
                        unseenCount > 0
                            ? `${unseenCount} new messages, scroll to bottom`
                            : 'Scroll to bottom'
                    }
                >
                    <ChevronDown aria-hidden="true" />
                    {unseenCount > 0 && (
                        <span className="op-jump-count">
                            {unseenCount > 9 ? '9+' : unseenCount}
                        </span>
                    )}
                </button>
            )}
        </>
    );
}
